import React, { useState, useEffect } from 'react';
import { X, FileText, Search, TrendingUp, Clock, Target, Star, Dumbbell, Users } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';

export default function SelectTemplateModal({ isOpen, onClose, member, onTemplateSelected }) {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchGroups();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      console.log('[SELECT_TEMPLATE] Fetching templates...');
      const response = await api.get('/gym_workout_templates.php?action=list');
      console.log('[SELECT_TEMPLATE] Templates received:', response.data);
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('[SELECT_TEMPLATE] Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      console.log('[SELECT_TEMPLATE] Fetching groups...');
      const response = await api.get('/gym_template_groups.php?action=list');
      console.log('[SELECT_TEMPLATE] Groups received:', response.data);
      setGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('[SELECT_TEMPLATE] Error fetching groups:', error);
      setGroups([]);
    }
  };

  const filteredTemplates = templates.filter(template => {
    // Filtro per gruppo: i template potrebbero essere in più gruppi, quindi controlliamo se il template appartiene al gruppo selezionato
    // Per ora filtriamo solo per nome/descrizione, il filtro gruppo sarà implementato quando l'API supporterà group_id nel filtro
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleConfirm = () => {
    if (selectedTemplate) {
      console.log('[SELECT_TEMPLATE] Template selected:', selectedTemplate);
      onTemplateSelected(selectedTemplate);
    }
  };

  const getDifficultyColor = (level) => {
    switch(level) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'expert': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getDifficultyLabel = (level) => {
    switch(level) {
      case 'beginner': return 'Principiante';
      case 'intermediate': return 'Intermedio';
      case 'advanced': return 'Avanzato';
      case 'expert': return 'Esperto';
      default: return level;
    }
  };

  const getTargetGoalLabel = (goal) => {
    switch(goal) {
      case 'strength': return 'Forza';
      case 'hypertrophy': return 'Ipertrofia';
      case 'endurance': return 'Resistenza';
      case 'flexibility': return 'Flessibilità';
      case 'general': return 'Generale';
      default: return goal;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex h-[90vh] w-[90vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
          <div className="min-w-0 pr-2">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
              <FileText className="h-6 w-6 shrink-0" />
              Seleziona Template
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-white/75">
              Assegna una scheda da template a <span className="font-semibold">{member?.name}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-600 transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="border-b border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
              />
            </div>

            {/* Group Filter */}
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
            >
              <option value="all">Tutti i Gruppi</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-y-auto bg-white p-6 dark:bg-gray-900">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {searchTerm || selectedGroup !== 'all' 
                  ? 'Nessun template trovato con i filtri selezionati' 
                  : 'Nessun template disponibile'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className={`cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-lg ${
                    selectedTemplate?.id === template.id
                      ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-gray-950'
                      : 'border-gray-200 hover:border-indigo-300 dark:border-white/15 dark:hover:border-white/30'
                  }`}
                >
                  {/* Template Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                        {template.is_favorite === 1 && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                        {template.name}
                      </h3>
                      {/* I gruppi saranno mostrati quando l'API li includerà nella risposta */}
                    </div>
                  </div>

                  {/* Description */}
                  {template.description && template.description !== '0' && template.description.trim() !== '' && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {template.description}
                    </p>
                  )}

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(template.difficulty_level)}`}>
                      {getDifficultyLabel(template.difficulty_level)}
                    </span>
                    {template.target_goal && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        <Target className="h-3 w-3 inline mr-1" />
                        {getTargetGoalLabel(template.target_goal)}
                      </span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
                    <span className="flex items-center gap-1">
                      <Dumbbell className="h-4 w-4" />
                      {template.exercise_count || 0} esercizi
                    </span>
                    {template.estimated_duration && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {template.estimated_duration} min
                      </span>
                    )}
                  </div>

                  {/* Usage Stats */}
                  {template.times_used > 0 && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Usato {template.times_used} {template.times_used === 1 ? 'volta' : 'volte'}
                    </div>
                  )}

                  {/* Selected Indicator */}
                  {selectedTemplate?.id === template.id && (
                    <div className="mt-3 flex items-center justify-center">
                      <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                        ✓ Selezionato
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-6 dark:border-white/10 dark:bg-gray-800">
          <div className="text-sm text-gray-600 dark:text-white/85">
            {selectedTemplate ? (
              <span className="font-semibold">
                Template selezionato: <span className="text-indigo-600 dark:text-indigo-400">{selectedTemplate.name}</span>
              </span>
            ) : (
              'Seleziona un template per continuare'
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedTemplate}
              className={`flex items-center gap-2 rounded-lg px-6 py-2 transition-colors ${
                selectedTemplate
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-800/50 dark:text-white/50'
              }`}
            >
              <Users className="h-4 w-4" />
              Assegna Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

